import React, { useEffect, useState } from 'react';
import { Users, Plus, Search, Edit, Trash2 } from 'lucide-react';
import clientService from '../services/clientService';
import { ClienteResponseDTO } from '../types/client';
import alertService from '../services/alertService';
import { useNavigate } from 'react-router-dom';

const ClientsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState<ClienteResponseDTO[]>([]);
  const [search, setSearch] = useState('');
  const [documento, setDocumento] = useState('');
  const [page, setPage] = useState(0);
  const [tipoPessoa, setTipoPessoa] = useState<'FISICA' | 'JURIDICA'>('FISICA');

  const formatCpf = (value: string): string => {
    const v = (value || '').replace(/[^\d]/g, '').slice(0, 11);
    if (v.length <= 3) return v;
    if (v.length <= 6) return v.replace(/(\d{3})(\d+)/, '$1.$2');
    if (v.length <= 9) return v.replace(/(\d{3})(\d{3})(\d+)/, '$1.$2.$3');
    return v.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, '$1.$2.$3-$4');
  };

  const formatCnpj = (value: string): string => {
    const v = (value || '').replace(/[^\d]/g, '').slice(0, 14);
    if (v.length <= 2) return v;
    if (v.length <= 5) return v.replace(/(\d{2})(\d+)/, '$1.$2');
    if (v.length <= 8) return v.replace(/(\d{2})(\d{3})(\d+)/, '$1.$2.$3');
    if (v.length <= 12) return v.replace(/(\d{2})(\d{3})(\d{3})(\d+)/, '$1.$2.$3/$4');
    return v.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, '$1.$2.$3/$4-$5');
  };

  const formatDocumentoTabela = (doc?: string) => {
    const clean = (doc || '').replace(/[^\d]/g, '');
    if (!clean) return '-';
    if (clean.length === 11) return formatCpf(clean);
    if (clean.length === 14) return formatCnpj(clean);
    return doc || '-';
  };

  const formatDocumentoCliente = (c: any) => {
    const raw = (c?.cpf || c?.cnpj || c?.documento || '') as string;
    const clean = (raw || '').replace(/[^\d]/g, '');
    if (!clean) return '-';
    if (clean.length === 11) return formatCpf(clean);
    if (clean.length === 14) return formatCnpj(clean);
    return '-';
  };

  const tipoPessoaLabel = (c: any) => {
    const tipo = c?.tipoPessoa;
    if (tipo === 'FISICA' || tipo === 'PF') return 'Pessoa Física';
    if (tipo === 'JURIDICA' || tipo === 'PJ') return 'Pessoa Jurídica';
    const hasCpf = !!c?.cpf;
    const hasCnpj = !!c?.cnpj;
    if (hasCpf) return 'Pessoa Física';
    if (hasCnpj) return 'Pessoa Jurídica';
    const doc = (c?.documento || '') as string;
    const clean = doc.replace(/[^\d]/g, '');
    if (clean.length === 11) return 'Pessoa Física';
    if (clean.length === 14) return 'Pessoa Jurídica';
    return '-';
  };

  const load = async () => {
    try {
      setLoading(true);
      const res = await clientService.list({ page, size: 10, sort: 'nome', direction: 'asc', filters: { nome: search, documento } });
      const list = res?.data?.content ?? [];
      setClients(list);
    } catch (err: any) {
      alertService.error('Erro ao carregar clientes', err?.message || 'Tente novamente');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Carregamento inicial e quando mudar de página
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleBuscar = () => {
    setPage(0);
    load();
  };

  const onDelete = async (id: string) => {
    const confirm = await alertService.confirm('Excluir cliente', 'Tem certeza que deseja excluir este cliente?');
    if (!confirm.isConfirmed) return;
    try {
      await clientService.delete(id);
      alertService.success('Cliente excluído');
      load();
    } catch (err: any) {
      alertService.error('Erro ao excluir', err?.message || 'Tente novamente');
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Users className="h-6 w-6 text-red-600" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">Clientes</h1>
            <p className="text-sm text-gray-600">Gerencie seus clientes</p>
          </div>
        </div>
        <button onClick={() => navigate('/clientes/novo')} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2">
          <Plus className="h-4 w-4" /> Novo Cliente
        </button>
      </div>

      {/* Busca */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <div className="grid grid-cols-12 gap-4 items-end">
          <div className="col-span-12 md:col-span-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">Documento</label>
            <div className="flex items-center gap-4 mb-2">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input type="radio" checked={tipoPessoa === 'FISICA'} onChange={() => setTipoPessoa('FISICA')} />
                <span>Pessoa Física</span>
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input type="radio" checked={tipoPessoa === 'JURIDICA'} onChange={() => setTipoPessoa('JURIDICA')} />
                <span>Pessoa Jurídica</span>
              </label>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
              {tipoPessoa === 'FISICA' ? (
                <input value={formatCpf(documento)} onChange={(e) => setDocumento(formatCpf(e.target.value))} placeholder="Digite o CPF" className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg" />
              ) : (
                <input value={formatCnpj(documento)} onChange={(e) => setDocumento(formatCnpj(e.target.value))} placeholder="Digite o CNPJ" className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg" />
              )}
            </div>
          </div>
          <div className="col-span-12 md:col-span-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">Nome (opcional)</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filtrar por nome" className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg" />
            </div>
          </div>
          <div className="col-span-12 md:col-span-2">
            <button onClick={handleBuscar} className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg">Buscar</button>
            <p className="mt-2 text-xs text-gray-500">Você pode buscar por CPF/CNPJ ou apenas carregar todos.</p>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Documento</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telefone</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {clients.map((c) => (
              <tr key={c.id}>
                <td className="px-6 py-4 text-sm text-gray-900">{c.nome}</td>
                <td className="px-6 py-4 text-sm text-gray-700">{tipoPessoaLabel(c as any)}</td>
                <td className="px-6 py-4 text-sm text-gray-700">{formatDocumentoCliente(c as any)}</td>
                <td className="px-6 py-4 text-sm text-gray-700">{c.telefone || '-'}</td>
                <td className="px-6 py-4 text-sm text-gray-700 text-right">
                  <button onClick={() => navigate(`/clientes/${c.id}`)} className="inline-flex items-center gap-1 px-3 py-1 border rounded mr-2">
                    <Edit className="h-4 w-4" /> Editar
                  </button>
                  <button onClick={() => onDelete(c.id)} className="inline-flex items-center gap-1 px-3 py-1 border rounded text-red-600">
                    <Trash2 className="h-4 w-4" /> Excluir
                  </button>
                </td>
              </tr>
            ))}
            {!loading && clients.length === 0 && (
              <tr>
                <td className="px-6 py-8 text-center text-gray-500" colSpan={4}>Nenhum cliente encontrado</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ClientsPage;